import { useEffect } from 'react';
import { Store } from 'components/common/types/Store.types';
import { useDispatch, useSelector } from 'react-redux';
import { DealStatus, DealType } from 'components/common/types/Deal.types';
import { changeOnlyProgrammaticDealData } from 'store/dealManagement/reducer';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { isDspDisabled as getIsDspDisabled } from 'utils/isDspDisabled';
import { isDspSupportingBidstream, isDspSupportingSync } from 'utils/isDspSupporting';

export interface UseDspSettingsResult {
  isDspSettingsActive: boolean;
  isDspDisabled: boolean;
  isSelectedDspSupportingCreativeSubmissionInBidStream: boolean;
  isSelectedDspSupportingSync: boolean;
  isSelectedDspSupportingOpenMarketplace: boolean;
  isCreativeSubmissionInBidStreamEnabled: boolean;
  isCreativeSubmissionInBidStreamEditable: boolean;
  isWithDspSync: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useDspSettings = (): UseDspSettingsResult => {
  const dispatch = useDispatch();
  const dealConfig = useSelector((state: Store) => state.dealConfig);
  const { dsp, syncWithDsp: isWithDspSync } = useSelector((state: Store) => state.dealManagement.programmatic);
  const dealType = useSelector(getDealType);
  const isCreativeSubmissionInBidStreamEnabled = useSelector(
    (state: Store) => state.dealManagement.programmatic.enableCreativeSubmissionInBidStream,
  );
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);

  const dspsThatSupportCreativeSubmissionInBidStream = dealConfig.dspsSupportingCreativeSubmissionInBidStream || [];
  const isSelectedDspSupportingCreativeSubmissionInBidStream = isDspSupportingBidstream(
    dsp,
    dealType,
    dspsThatSupportCreativeSubmissionInBidStream,
  );

  const dspsThatSupportSync = dealConfig.dspsThatSupportSynchronisation || [];
  const isSelectedDspSupportingSync = isDspSupportingSync(dsp, dspsThatSupportSync);

  const dspsThatSupportOpenMarketplace = dealConfig.enableOpenMarketplace || [];
  const isSelectedDspSupportingOpenMarketplace =
    !!dsp && dealType === DealType.NON_GUARANTEED_FLOOR_PRICE && dspsThatSupportOpenMarketplace.includes(dsp.code);

  const isDspSettingsActive =
    isSelectedDspSupportingSync ||
    isSelectedDspSupportingCreativeSubmissionInBidStream ||
    isSelectedDspSupportingOpenMarketplace;

  const isLive = bookingStatusCode === DealStatus.LIVE;
  const isRejected = bookingStatusCode === DealStatus.REJECTED;

  const isCreativeSubmissionInBidStreamEditable =
    isSelectedDspSupportingCreativeSubmissionInBidStream && (isLive || isRejected);
  const isDspDisabled = getIsDspDisabled(bookingStatusCode);

  useEffect(() => {
    if (!isSelectedDspSupportingCreativeSubmissionInBidStream && isCreativeSubmissionInBidStreamEnabled) {
      dispatch(
        changeOnlyProgrammaticDealData({
          enableCreativeSubmissionInBidStream: false,
        }),
      );
    }
  }, [isSelectedDspSupportingCreativeSubmissionInBidStream, isCreativeSubmissionInBidStreamEnabled]);

  useEffect(() => {
    if (isSelectedDspSupportingSync && !isWithDspSync) {
      dispatch(changeOnlyProgrammaticDealData({ syncWithDsp: true }));
    }
  }, [isSelectedDspSupportingSync, isWithDspSync, dispatch]);

  return {
    isDspSettingsActive,
    isDspDisabled,
    isSelectedDspSupportingCreativeSubmissionInBidStream,
    isSelectedDspSupportingSync,
    isSelectedDspSupportingOpenMarketplace,
    isCreativeSubmissionInBidStreamEnabled,
    isCreativeSubmissionInBidStreamEditable,
    isWithDspSync,
  };
};
